<template>
  <div :class="getItemTableContainerClass">
    <ItemTable
      ref="itemTable"
      :boundItemsUrl="boundItemsUrl"
      :collectionFilters="collectionFilters"
      :columns="columns"
      :details="inMainPanel"
      :gettingStartedItemType="ldap"
      :handleDetailsButtonClick="handleDetailsButtonClick"
      :handleMainAction="handleMainAction"
      :handleRowClick="handleRowClick"
      :handleUnselectLockedItem="handleUnselectLockedDirectoryItem"
      :handleUnselectMultipleLockedItems="handleUnselectMultipleDirectoryLockedItems"
      :hasSelectColumn="hasSelectColumn"
      :inDetailsAsidePanel="inDetailsAsidePanel"
      :isAsideOpen="isAsideOpen"
      :isItemLocked="isDirectoryItemLocked"
      :isMainActionDisabled="isMainActionDisabled"
      :itemType="itemType"
      :keepFresh="inMainPanel"
      :mainActionDisabledMessage="mainActionDisabledMessage"
      :reportType="reportType"
      :selectionItemText="{
        label: inMainPanel ? 'selected' : 'bound',
        plural: 'directories',
        singular: 'directory'
      }"
      :setGettingStartedTooltipVisibility="setGettingStartedTooltipVisibility"
      :showBound="false"
      :showMainAction="inMainPanel"
      :showSearchForm="false"
    />
    <Modal
      v-model="modalVisible.ldapBindingUser"
      cancelText="cancel"
      :handleMainAction="handleUnbindLdapBindingUser"
      headerText="LDAP Bind DN"
      mainActionText="continue"
    >
      <p>
        This user has been enabled as an LDAP Bind DN. Removing them from the JumpCloud
        LDAP Directory will also remove their Bind DN privileges.
      </p>
    </Modal>
    <Modal
      v-model="modalVisible.unableToRemoveUser"
      cancelText=""
      :handleMainAction="() => closeModal(modalTypes.unableToRemoveUser)"
      headerText="Unable to Remove User"
      mainActionText="OK"
    >
      <p>
        This user is the active Samba Service Account. To remove them from LDAP, you
        must designate another Samba Service Account or disable Samba authentication
        in the JumpCloud LDAP Configuration.
      </p>
    </Modal>
  </div>
</template>
<script>

import {
  DirectoryGroupingConsts,
  ItemTableConsts,
  ItemTypeConsts,
  StoreModuleMap,
  getDetailsRoute,
} from 'client/util/Constants';
import {
  DirectoryTypeConsts,
} from 'client/util/Constants/DirectoryConstants';
import { ReportTypes } from 'client/Reports/ReportMetadata';
import { mapActions, mapGetters } from 'vuex';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import DirectoryApi from 'client/api/v2/DirectoryApi';
import DirectoryItemTableMethodsMixin from 'client/directories/components/DirectoryItemTableMethodsMixin';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import ItemTable from 'client/components/ItemTable/ItemTable.vue';
import ItemTableComputedMixin from 'client/components/ItemTable/mixins/ItemTableComputedMixin';
import ItemTableIndirectBindingMixin from 'client/components/ItemTable/mixins/ItemTableIndirectBindingMixin';
import ItemTableMethodsMixin from 'client/components/ItemTable/mixins/ItemTableMethodsMixin';
import ItemTablePropsMixin from 'client/components/ItemTable/mixins/ItemTablePropsMixin';
import Modal from 'client/components/Modal/Modal.vue';
import Util from 'client/util/Util';

const { columnTypes } = ItemTableConsts;
const { groupAsidePanel, mainPanel, userAsidePanel } = ItemTableConsts.panelType;
const directoryCollection = StoreModuleMap[ItemTypeConsts.directory];
const ldapBindingUser = 'ldapBindingUser';
const mainActionDisabledMessage = 'Only one instance of LDAP is allowed.';
const unableToRemoveUser = 'unableToRemoveUser';

export default {
  name: 'LdapItemTable',

  components: {
    ItemTable,
    Modal,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
    DirectoryItemTableMethodsMixin,
    ItemTableComputedMixin,
    ItemTableIndirectBindingMixin,
    ItemTableMethodsMixin,
    ItemTablePropsMixin,
  ],

  props: {
    gettingStartedItemType: {
      type: String,
      default: ItemTypeConsts.ldap,
    },
    onLdapDirectoryUnselected: {
      default: () => {},
      type: Function,
    },
    panelType: {
      default: mainPanel,
      type: String,
      required: false,
    },
  },

  data() {
    let hasSelectColumn = false;
    const collectionFilters = [];
    const { groupTypes, memberTypes } = DirectoryGroupingConsts;
    let groupType;
    let memberType;

    if (this.inUserAside()) {
      groupType = groupTypes.userGroup;
      memberType = memberTypes.user;
    }

    const columns = [
      {
        bodyComponent: 'DirectoryTypeIcon',
        classNames: {
          body: [columnTypes.iconColumn],
          heading: [columnTypes.iconColumn],
        },
        headingText: 'Type',
      },
      {
        bodyComponent: 'ItemName',
        bodyComponentProps: {
          titleFunction: item => this.titleFunction(item),
        },
        classNames: {
          body: [this.$style.nameColumn],
        },
        headingText: 'Name',
        sortBy: 'name',
      },
    ];

    if (this.inDetailsAsidePanel) {
      // Allow directories to be selected in Aside Panels
      hasSelectColumn = true;
    }

    // Show the access configurations column in the User Aside Panel
    if (this.inUserAside()) {
      columns.push({
        bodyComponent: 'DirectoryAccessConfiguration',
        bodyComponentProps: {
          parentType: this.parentType,
        },
        headingText: 'Access Configurations',
      });
    }

    return {
      collectionFilters,
      columns,
      groupType,
      hasSelectColumn,
      itemType: ItemTypeConsts.directory,
      ldap: ItemTypeConsts.ldap,
      mainActionDisabledMessage,
      memberType,
      modalVisible: {
        [ldapBindingUser]: false,
        [unableToRemoveUser]: false,
      },
      modalTypes: {
        ldapBindingUser,
        unableToRemoveUser,
      },
      reportType: ReportTypes.userToLdap,
    };
  },

  computed: {
    ...mapGetters(directoryCollection, {
      isItemPreviouslySelected: 'isItemPreviouslySelected',
      items: 'getCollection',
      previouslyDirectlyBoundItems: 'getPreviouslyDirectlyBoundItems',
      previouslyDirectlyBoundItemsCount: 'getPreviouslyDirectlyBoundItemsCount',
      selectedItems: 'getSelectedItems',
      selectedItemsCount: 'getSelectedItemsCount',
    }),

    isMainActionDisabled() {
      return this.getTotalCount() > 0;
    },
  },

  beforeCreate() {
    const { panelType } = this.$options.propsData;

    if (panelType === groupAsidePanel) {
      this.$options.methods.handleBeforeCreate(this.$options, directoryCollection);
    } else if (panelType === userAsidePanel) {
      this.$options.methods.handleBeforeCreate(this.$options, directoryCollection);
      this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options, true);
    }
  },

  /**
   * When the DirectoryItemTable is rendered in the User or Directory Aside Panel register
   * watchers to check if LDAP was unselected when either the direct binding to an item that is
   * both indirectly and directly bound to the parent is removed, and when a directly bound item is
   * unselected.
   */
  created() {
    if (this.inGroupAside() || this.inUserAside()) {
      this.$watch('previouslyDirectlyBoundItemsCount', this.checkIfLdapDirectBindingWasRemoved);
      this.$watch('selectedItemsCount', this.checkIfLdapWasUnselected);
    }
  },

  beforeMount() {
    this.setCollectionFilters();
  },

  methods: {
    ...mapActions(directoryCollection, [
      'selectItem',
      'unselectItem',
    ]),

    ...mapGetters(directoryCollection, [
      'getItem',
      'getLdap',
      'isActiveDirectory',
      'isLdap',
      'getTotalCount',
    ]),

    handleDetailsButtonClick(itemId) {
      if (this.inMainPanel) {
        const item = this.getItem()(itemId);
        const url = getDetailsRoute(item.type, item);

        Util.redirect(url);
      }
    },

    handleMainAction() {
      EventHelper.triggerAddNewLdapButtonClicked();

      DirectoryApi
        .getLdap()
        .then((resp) => {
          const url = getDetailsRoute(resp.type, resp);
          Util.redirect(url);
        });
    },

    /**
     * The collection filters are set in the beforeMount lifecycle hook because a beta flag is
     * checked to determine which filters to set and the beta flag is not available when the data
     * is initially populated. Collection filters are a prop that is passed to the ItemTable and in
     * turn to the store and Api instance. They are used to determine which directories are
     * filtered out of the GET /api/v2/directories request - based on the panel the table is
     * rendered in.
     */
    setCollectionFilters() {
      this.collectionFilters.push({
        name: 'type',
        operator: 'eq',
        value: `${DirectoryTypeConsts.ldapServer}`,
      });
      if (this.panelType === mainPanel) {
        // Only show activated directories in the Main Panel
        this.collectionFilters.push({
          name: 'activated',
          operator: 'eq',
          value: true,
        });
      } else {
        // Don't show the Workday Import in Aside Panels
        this.collectionFilters.push({
          name: 'type',
          operator: 'ne',
          value: `${DirectoryTypeConsts.workday}`,
        });
      }
    },

    titleFunction(item) {
      return item.name;
    },
  },
};
</script>

<style module>
.nameColumn {
  max-width: 350px;
}
</style>
